// src/pages/contentManagement/audios/audioList.js
import React, { useCallback, useEffect, useMemo, useReducer, useState } from "react";
import { Button, Collapse, Divider, Form, Input, Modal, Pagination, Table, Tooltip, Upload } from "antd";
import { FaTrash, FaUpload, FaChevronDown, FaInfoCircle } from "react-icons/fa";
import axios from "../../../services/axiosConfig";
import { API_ENDPOINTS } from "../../../services/apiEndpoints";
import { getAuthToken } from "../../../auth/auth";
import useNotification from "../../../hooks/useNotification";
import Layout from "../../../components/layout/layout";
import TableLayout from "../../../components/tableLayout/tableLayout";
import TopToolBar from "../../../components/topToolBar/topToolBar";
import "./audioList.css";

const { Panel } = Collapse;

// Initial state for useReducer
const initialState = {
  isLoading: true,
  searchTerm: "",
  filters: {},
  audioFiles: [],
  currentPage: 1,
  pageSize: 10,
  sortConfig: { key: "uploadedAt", direction: "desc" },
  showDeleteModal: false,
  deleteFileId: null
};

// Reducer for state management
const audioReducer = (state, action) => {
  switch (action.type) {
    case "setLoading":
      return { ...state, isLoading: action.payload };
    case "setSearchTerm":
      return { ...state, searchTerm: action.payload, currentPage: 1 };
    case "setFilters":
      return { ...state, filters: action.payload, currentPage: 1 };
    case "setAudioFiles":
      return { ...state, audioFiles: action.payload };
    case "setPagination":
      return {
        ...state,
        currentPage: action.payload.page,
        pageSize: action.payload.pageSize
      };
    case "setSortConfig":
      return { ...state, sortConfig: action.payload };
    case "setDeleteModal":
      return {
        ...state,
        showDeleteModal: action.payload.show,
        deleteFileId: action.payload.fileId || null
      };
    case "removeAudioFile":
      return {
        ...state,
        audioFiles: state.audioFiles.filter((file) => file.id !== action.payload)
      };
    case "addAudioFile":
      return {
        ...state,
        audioFiles: [action.payload, ...state.audioFiles]
      };
    case "resetFilters":
      return {
        ...state,
        filters: {},
        searchTerm: "",
        currentPage: 1
      };
    default:
      return state;
  }
};

function AudioManagement() {
  const { notification, showNotification, hideNotification } = useNotification();
  const [state, dispatch] = useReducer(audioReducer, initialState);
  const [form] = Form.useForm();

  // Additional state for upload and notifications
  const [uploadLoading, setUploadLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [duration, setDuration] = useState("");

  const { isLoading, searchTerm, filters, audioFiles, currentPage, pageSize, sortConfig, showDeleteModal, deleteFileId } = state;

  // Configuration objects
  const filtersConfig = useMemo(
    () => ({
      uploadDate: {
        label: "Upload Date",
        options: [
          { value: "newest", label: "Newest" },
          { value: "last7Days", label: "Last 7 Days" },
          { value: "last30Days", label: "Last 30 Days" },
          { value: "oldest", label: "Oldest" }
        ]
      },
      durationType: {
        label: "Duration",
        options: [
          { value: "short", label: "Short (< 5 min)" },
          { value: "medium", label: "Medium (5-15 min)" },
          { value: "long", label: "Long (> 15 min)" }
        ]
      }
    }),
    []
  );

  // Date filtering utility
  const filterByDate = useCallback((items, dateFilter) => {
    if (!dateFilter) return items;

    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    switch (dateFilter) {
      case "newest":
        return [...items].sort((a, b) => new Date(b.uploadedAt) - new Date(a.uploadedAt));
      case "oldest":
        return [...items].sort((a, b) => new Date(a.uploadedAt) - new Date(b.uploadedAt));
      case "last7Days": {
        const sevenDaysAgo = new Date(today);
        sevenDaysAgo.setDate(today.getDate() - 7);
        return items.filter((item) => new Date(item.uploadedAt) >= sevenDaysAgo);
      }
      case "last30Days": {
        const thirtyDaysAgo = new Date(today);
        thirtyDaysAgo.setDate(today.getDate() - 30);
        return items.filter((item) => new Date(item.uploadedAt) >= thirtyDaysAgo);
      }
      default:
        return items;
    }
  }, []);

  // Global search function
  const globalSearch = useCallback((items, term) => {
    if (!term?.trim()) return items;

    const searchLower = term.toLowerCase().trim();

    return items.filter((item) => {
      const searchFields = [
        item.fileName || "",
        item.duration?.toString() || "",
        item.uploadedAt
          ? new Date(item.uploadedAt)
              .toLocaleDateString("en-GB", {
                timeZone: "Asia/Kolkata",
                day: "2-digit",
                month: "short",
                year: "numeric"
              })
              .replace(/ /g, "-")
          : ""
      ];

      return searchFields.some((field) => field.toLowerCase().includes(searchLower));
    });
  }, []);

  // Apply all filters and search
  const filteredData = useMemo(() => {
    let result = [...audioFiles];

    // Apply date filter first (includes sorting)
    result = filterByDate(result, filters.uploadDate);

    // Apply duration type filter
    if (filters.durationType) {
      switch (filters.durationType) {
        case "short":
          result = result.filter((file) => parseFloat(file.duration) < 5);
          break;
        case "medium":
          result = result.filter((file) => {
            const dur = parseFloat(file.duration);
            return dur >= 5 && dur <= 15;
          });
          break;
        case "long":
          result = result.filter((file) => parseFloat(file.duration) > 15);
          break;
        default:
          break;
      }
    }

    // Apply global search
    result = globalSearch(result, searchTerm);

    // Apply sorting if no date filter is applied
    if (!filters.uploadDate && sortConfig) {
      result.sort((a, b) => {
        const { key, direction } = sortConfig;
        if (key === "uploadedAt") {
          return direction === "asc" ? new Date(a.uploadedAt) - new Date(b.uploadedAt) : new Date(b.uploadedAt) - new Date(a.uploadedAt);
        }
        if (key === "duration") {
          return direction === "asc" ? parseFloat(a.duration || 0) - parseFloat(b.duration || 0) : parseFloat(b.duration || 0) - parseFloat(a.duration || 0);
        }
        const valA = a[key]?.toString().toLowerCase() || "";
        const valB = b[key]?.toString().toLowerCase() || "";
        if (valA < valB) return direction === "asc" ? -1 : 1;
        if (valA > valB) return direction === "asc" ? 1 : -1;
        return 0;
      });
    }

    return result;
  }, [audioFiles, filters, searchTerm, filterByDate, globalSearch, sortConfig]);

  // Get current page data
  const currentPageData = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return filteredData.slice(startIndex, endIndex);
  }, [filteredData, currentPage, pageSize]);

  // Fetch Audio Files
  const fetchAudioFiles = useCallback(async () => {
    dispatch({ type: "setLoading", payload: true });
    try {
      const token = await getAuthToken();
      const response = await axios.get(API_ENDPOINTS.AUDIO_FILES, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.status === 200 && Array.isArray(response.data)) {
        dispatch({ type: "setAudioFiles", payload: response.data });
      } else {
        throw new Error("Unexpected API response format.");
      }
    } catch (error) {
      console.error("Error fetching audio files:", error);
      showNotification("Failed to load audio files", "error");
    } finally {
      dispatch({ type: "setLoading", payload: false });
    }
  }, [showNotification]);

  // Handle File Selection
  const handleFileChange = useCallback(
    (file) => {
      if (file.type !== "audio/mpeg") {
        showNotification("Only MP3 files are allowed. Please select a valid MP3 file.", "error");
        return false;
      }

      setSelectedFile(file);

      const audio = new Audio(URL.createObjectURL(file));

      audio.addEventListener("loadedmetadata", () => {
        if (audio.duration) {
          const calculatedDuration = (audio.duration / 60).toFixed(2);
          setDuration(calculatedDuration);
          // Update the form field value
          form.setFieldsValue({ duration: calculatedDuration });
        } else {
          setDuration("");
          form.setFieldsValue({ duration: "" });
          showNotification("Could not read audio duration. Please try another file.", "error");
        }
      });

      return false; // Prevent auto upload
    },
    [showNotification, form]
  );

  // Handle Upload
  const handleUpload = useCallback(async () => {
    if (!selectedFile) {
      showNotification("Please select a file.", "error");
      return;
    }

    setUploadLoading(true);

    try {
      const token = await getAuthToken();

      // Step 1: Get Pre-signed URL from the backend
      const preSignedResponse = await axios.post(
        `${API_ENDPOINTS.AUDIO_FILES}/presigned-url`,
        {
          fileName: selectedFile.name,
          fileType: selectedFile.type,
          duration: duration
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (preSignedResponse.status !== 200) {
        throw new Error("Failed to get a pre-signed URL.");
      }

      const { url, id, s3Key } = preSignedResponse.data;

      // Step 2: Upload the file directly to S3 (no auth header needed)
      await axios.put(url, selectedFile, {
        headers: {
          "Content-Type": selectedFile.type
        }
      });

      // Step 3: Save metadata to the backend
      const metadataPayload = {
        id,
        fileName: selectedFile.name,
        fileType: selectedFile.type,
        duration,
        url,
        s3Key
      };

      const metadataResponse = await axios.post(`${API_ENDPOINTS.AUDIO_FILES}/metadata`, metadataPayload, { headers: { Authorization: `Bearer ${token}` } });

      if (metadataResponse.status === 200 || metadataResponse.status === 201) {
        showNotification("File uploaded successfully!", "success");
        setSelectedFile(null);
        setDuration("");
        form.resetFields();
        await fetchAudioFiles();
      } else {
        throw new Error("Failed to save file metadata.");
      }
    } catch (error) {
      console.error("Upload failed:", error.response?.data || error.message);
      showNotification(error.response?.data?.message || "Upload failed. Please try again.", "error");
    } finally {
      setUploadLoading(false);
    }
  }, [selectedFile, duration, fetchAudioFiles, showNotification, form]);

  // Handle Cancel - Clear form
  const handleCancel = useCallback(() => {
    setSelectedFile(null);
    setDuration("");
    form.resetFields();
    showNotification("Form cleared.", "info");
  }, [form, showNotification]);

  // Handle Delete
  const confirmDelete = useCallback((id) => {
    dispatch({
      type: "setDeleteModal",
      payload: { show: true, fileId: id }
    });
  }, []);

  const handleDelete = useCallback(async () => {
    if (!deleteFileId) return;

    dispatch({ type: "setDeleteModal", payload: { show: false } });
    dispatch({ type: "setLoading", payload: true });

    try {
      const token = await getAuthToken();
      const response = await axios.delete(`${API_ENDPOINTS.AUDIO_FILES}/${deleteFileId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.status === 200 || response.status === 204) {
        dispatch({ type: "removeAudioFile", payload: deleteFileId });
        showNotification("Audio file deleted successfully.", "success");
      } else {
        throw new Error("Failed to delete the file.");
      }
    } catch (error) {
      console.error("Error deleting the file:", error);
      showNotification("Failed to delete the file.", "error");
    } finally {
      dispatch({ type: "setLoading", payload: false });
    }
  }, [deleteFileId, showNotification]);

  const handleDeleteModalCancel = useCallback(() => {
    dispatch({ type: "setDeleteModal", payload: { show: false } });
  }, []);

  // Event handlers
  const handleSearch = useCallback((term) => {
    dispatch({ type: "setSearchTerm", payload: term });
  }, []);

  const handleFilterChange = useCallback((newFilters) => {
    dispatch({ type: "setFilters", payload: newFilters });
  }, []);

  const handleReset = useCallback(() => {
    dispatch({ type: "resetFilters" });
  }, []);

  const handlePaginationChange = useCallback((page, size) => {
    dispatch({ type: "setPagination", payload: { page, pageSize: size } });
  }, []);

  // Table columns configuration
  const columns = useMemo(
    () => [
      {
        title: "SL. NO.",
        render: (_, __, index) => (currentPage - 1) * pageSize + index + 1,
        width: 80
      },
      {
        title: "AUDIO NAME",
        dataIndex: "fileName",
        sorter: (a, b) => (a.fileName || "").localeCompare(b.fileName || ""),
        render: (fileName) => fileName || "-"
      },
      {
        title: "UPLOADED DATE",
        dataIndex: "uploadedDate",
        sorter: (a, b) => new Date(a.uploadedDate) - new Date(b.uploadedDate),
        render: (date) =>
          date
            ? new Date(date)
                .toLocaleDateString("en-GB", {
                  timeZone: "Asia/Kolkata",
                  day: "2-digit",
                  month: "short",
                  year: "numeric"
                })
                .replace(/ /g, "-")
            : "-"
      },
      {
        title: "DURATION",
        dataIndex: "duration",
        sorter: (a, b) => parseFloat(a.duration || 0) - parseFloat(b.duration || 0),
        render: (duration) => (duration ? `${duration} min` : "-"),
        width: 120
      },
      {
        title: "ACTIONS",
        render: (_, record) => (
          <div className="audm-list-btn-group" onClick={(e) => e.stopPropagation()}>
            <Tooltip title="Delete">
              <button className="audm-list-delete-btn" onClick={() => confirmDelete(record.id)}>
                <FaTrash />
              </button>
            </Tooltip>
          </div>
        ),
        width: 120
      }
    ],
    [currentPage, pageSize, confirmDelete]
  );

  // Excel export data
  const prepareExcelData = useCallback(() => {
    return filteredData.map((file, index) => ({
      "SL. NO.": index + 1,
      Type: "Audio",
      "Audio Name": file.fileName,
      "Uploaded Date": file.uploadedAt
        ? new Date(file.uploadedAt)
            .toLocaleDateString("en-GB", {
              timeZone: "Asia/Kolkata",
              day: "2-digit",
              month: "short",
              year: "numeric"
            })
            .replace(/ /g, "-")
        : "-",
      Duration: file.duration ? `${file.duration} min` : "-"
    }));
  }, [filteredData]);

  // Effects
  useEffect(() => {
    fetchAudioFiles();
  }, [fetchAudioFiles]);

  return (
    <Layout
      navTitle="Content Management"
      pageTitle="Audios"
      notificationProps={[
        {
          show: notification.show,
          message: notification.message,
          type: notification.type,
          onClose: hideNotification,
          duration: 4000
        }
      ]}
    >
      <div className="audm-upload-section">
        <Collapse
          expandIcon={({ isActive }) => (
            <FaChevronDown
              style={{
                transform: isActive ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 0.3s"
              }}
            />
          )}
          className="audm-upload-collapse"
        >
          <Panel header="Upload New Audio File" key="1">
            <Form form={form} layout="vertical" className="audm-upload-form">
              <Form.Item
                label="Upload Audio File"
                name="audioFile"
                tooltip={{
                  title: "Accepted formats: MP3",
                  icon: <FaInfoCircle style={{ cursor: "pointer" }} />
                }}
              >
                <Upload
                  className="audm-upload-input"
                  accept="audio/mpeg"
                  beforeUpload={handleFileChange}
                  fileList={selectedFile ? [selectedFile] : []}
                  onRemove={() => {
                    setSelectedFile(null);
                    setDuration("");
                    form.setFieldsValue({
                      audioFile: undefined,
                      duration: ""
                    });
                  }}
                  maxCount={1}
                >
                  <Button icon={<FaUpload />}>Select Audio File</Button>
                </Upload>
              </Form.Item>

              <Form.Item label="Duration (in minutes)" name="duration">
                <Input className="audm-upload-input" value={duration} readOnly placeholder="Auto-detected" style={{ width: "200px" }} />
              </Form.Item>

              <Form.Item>
                <div className="audm-form-btn-group">
                  <Button className="audm-form-cancel-btn" onClick={handleCancel} disabled={uploadLoading}>
                    Clear
                  </Button>
                  <Button className="audm-form-submit-btn" onClick={handleUpload} loading={uploadLoading} disabled={!selectedFile || uploadLoading}>
                    {uploadLoading ? "Uploading..." : "Upload"}
                  </Button>
                </div>
              </Form.Item>
            </Form>
          </Panel>
        </Collapse>
      </div>

      <Divider />

      <TableLayout
        toolbar={
          <TopToolBar
            filtersConfig={filtersConfig}
            searchPlaceholder="Search audio files..."
            searchTerm={searchTerm}
            filters={filters}
            onSearch={handleSearch}
            onFilterChange={handleFilterChange}
            onReset={handleReset}
            showExcelDownload={true}
            excelData={prepareExcelData()}
            excelFilename="audio-files"
            excelSheetName="Audio Files"
          />
        }
      >
        <Table
          className="tpl-table"
          rowKey={(record) => record.id}
          rowClassName={(record, index) => (index % 2 === 0 ? "table-row-light" : "table-row-dark")}
          columns={columns}
          dataSource={currentPageData}
          loading={isLoading}
          pagination={false}
          expandable={{
            showExpandColumn: false
          }}
        />
        <div className="tpl-pagination-wrapper">
          <Pagination className="tpl-pagination" current={currentPage} pageSize={pageSize} total={filteredData.length} onChange={handlePaginationChange} showSizeChanger={false} />
        </div>
      </TableLayout>

      {/* Delete Confirmation Modal */}
      <Modal
        title="Confirm Deletion"
        open={showDeleteModal}
        onCancel={handleDeleteModalCancel}
        footer={[
          <div className="audm-modal-btn-group" key="modal-buttons">
            <Button className="audm-modal-cancel-btn" key="cancel" onClick={handleDeleteModalCancel}>
              Cancel
            </Button>
            <Button className="audm-modal-delete-btn" key="delete" onClick={handleDelete}>
              Delete
            </Button>
          </div>
        ]}
        width={400}
        centered
      >
        <p>Are you sure you want to delete this audio file?</p>
      </Modal>
    </Layout>
  );
}

export default AudioManagement;
